import type { Action } from '@/app/actions/actions';
import type { ActionArgs, ActionSpec, ActionSpecRecord } from '@/app/actions/actionsSpec';
import { codeActionsSpec } from '@/app/actions/codeActionsSpec';
import { columnRowSpec } from '@/app/actions/columnRowSpec';
import { dataTableSpec } from '@/app/actions/dataTableSpec';
import { editActionsSpec } from '@/app/actions/editActionsSpec';
import { fileActionsSpec } from '@/app/actions/fileActionsSpec';
import { formatActionsSpec } from '@/app/actions/formatActionsSpec';
import { helpActionsSpec } from '@/app/actions/helpActionsSpec';
import { insertActionsSpec } from '@/app/actions/insertActionsSpec';
import { pageBreakActionsSpec } from '@/app/actions/pageBreakActionsSpec';
import { selectionActionsSpec } from '@/app/actions/selectionActionsSpec';
import { sheetAutoFilterSpec } from '@/app/actions/sheetAutoFilterSpec';
import { viewActionsSpec } from '@/app/actions/viewActionsSpec';

export const defaultActionSpec: ActionSpecRecord = {
  ...fileActionsSpec,
  ...editActionsSpec,
  ...viewActionsSpec,
  ...insertActionsSpec,
  ...formatActionsSpec,
  ...helpActionsSpec,
  ...selectionActionsSpec,
  ...codeActionsSpec,
  ...columnRowSpec,
  ...dataTableSpec,
  ...pageBreakActionsSpec,
  ...sheetAutoFilterSpec,
};

/**
 * Subset of `Action` whose `run(args)` accepts being called with `undefined`.
 *
 * An action qualifies when it is either not in `ActionArgs` (its arg type is
 * `void`) or its arg type is optional (the union includes `undefined`).
 * Actions that require a concrete argument (e.g. `FormatBorderAll` requiring
 * `UseBordersResults`) are excluded so the compiler rejects them at the
 * `runVoidAction` call site.
 */
export type VoidAction = {
  [K in Action]: K extends keyof ActionArgs ? (undefined extends ActionArgs[K] ? K : never) : K;
}[Action];

/**
 * Run an action that does not require arguments. Use this when dispatching
 * actions dynamically (e.g. from a generic toolbar) where the union of all
 * possible action arg types makes the indexed `run` signature inexpressible
 * without an explicit cast.
 *
 * For actions that require arguments, prefer indexing `defaultActionSpec`
 * directly with the literal Action so TypeScript can narrow the args type.
 */
export const runVoidAction = (action: VoidAction) => {
  const spec = defaultActionSpec[action] as ActionSpec<undefined>;
  spec.run(undefined);
};
